/**
 * Prime Magic Dashboard Overlay
 * Faithful reproduction of the MT4 panel with toggle support.
 * Layout: Title → Symbol/CMP → 2×2 signal grid → P&L row → Status row
 * CSS prefix: pm2-
 */
import React from "react";
import { PrimeMagicDashboard } from "../../indicators/built-in/prime-magic";
import "./PrimeMagicOverlay.scss";

interface PrimeMagicOverlayProps {
  dashboard: PrimeMagicDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short" }[];
}

const PrimeMagicOverlay: React.FC<PrimeMagicOverlayProps> = ({
  dashboard,
  symbol,
  visible,
  showDashboard = true,
  onToggleDashboard,
  arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const { signal, trend, entry, stopLoss, trailingStop, target1, target2,
    currentPrice, livePL, bookedPL, status, tp1Hit, tp2Hit, slHit, tslHit } = dashboard;

  const isLong = trend === "LONG";
  const isShort = trend === "SHORT";
  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => (v >= 0 ? `+${v.toFixed(2)}` : v.toFixed(2));

  const statusText =
    status === "Target1 Hit" ? "TGT-1 Hit"
    : status === "Target2 Hit" ? "TGT-2 Hit"
    : status === "Stop Loss Hit" ? "SL Hit"
    : status === "Trailing SL Hit" ? "TSL Hit"
    : "Open";

  const statusValueMod =
    status === "Active" ? ""
    : status.includes("Target") ? " pm2-status-value--tgt"
    : status === "Stop Loss Hit" ? " pm2-status-value--sl"
    : " pm2-status-value--tsl";

  return (
    <>
      {/* ─── Arrow Badges — positioned HTML labels at arrow bar coordinates ─── */}
      {arrowBadges.map((badge, i) => (
        <div
          key={i}
          className={`pm2-arrow-badge pm2-arrow-badge--${badge.direction}`}
          style={{
            left: badge.x,
            top: badge.direction === "long" ? badge.y + 30 : badge.y - 44,
          }}
        >
          {badge.direction === "long" ? "Buy" : "Sell"}
        </div>
      ))}

    <div className={`pm2-overlay ${isLong ? "pm2-buy" : isShort ? "pm2-sell" : "pm2-neutral"}${showDashboard ? "" : " pm2-overlay--collapsed"}`}>
      {/* Title with toggle */}
      <div className="pm2-title-row">
        <span className="pm2-title">Prime Magic</span>
        <button
          className="pm2-toggle-btn"
          onClick={onToggleDashboard}
          title={showDashboard ? "Hide dashboard" : "Show dashboard"}
          aria-label={showDashboard ? "Hide dashboard" : "Show dashboard"}
        >
          <span className={`pm2-toggle-icon${showDashboard ? " pm2-toggle-icon--open" : ""}`}>
            &#x25BE;
          </span>
        </button>
      </div>

      {showDashboard && (
        <>
      {/* Symbol + CMP row */}
      <div className="pm2-header-row">
        <span className="pm2-symbol">{symbol}</span>
        <span className="pm2-cmp">{currentPrice.toFixed(2)}</span>
      </div>

      {/* 2×2 Signal Grid — matches MT4 layout */}
      <div className="pm2-grid">
        <div className="pm2-grid-row">
          <div className="pm2-cell">
            <span className="pm2-cell-badge">{isShort ? "SELL" : "BUY"}</span>
            <span className="pm2-cell-value">{fmt(entry)}</span>
          </div>
          <div className="pm2-cell">
            <span className="pm2-cell-value">{fmt(stopLoss)}{slHit ? " ✓" : ""}</span>
            <span className="pm2-cell-badge">SL</span>
          </div>
        </div>
        <div className="pm2-grid-row">
          <div className="pm2-cell">
            <span className="pm2-cell-badge">TG-1</span>
            <span className={`pm2-cell-value ${tp1Hit ? "pm2-hit" : ""}`}>{fmt(target1)}{tp1Hit ? " ✓" : ""}</span>
          </div>
          <div className="pm2-cell">
            <span className={`pm2-cell-value ${tp2Hit ? "pm2-hit" : ""}`}>{fmt(target2)}{tp2Hit ? " ✓" : ""}</span>
            <span className="pm2-cell-badge">TG-2</span>
          </div>
        </div>
      </div>

      {/* P&L row */}
      <div className="pm2-pl-row">
        <div className="pm2-pl-cell">
          <span className="pm2-pl-label">Live P&amp;L</span>
          <span className={`pm2-pl-value ${livePL >= 0 ? "pm2-profit" : "pm2-loss"}`}>{fmtPL(livePL)}</span>
        </div>
        <div className="pm2-pl-cell">
          <span className="pm2-pl-label">Booked P&amp;L</span>
          <span className={`pm2-pl-value ${bookedPL >= 0 ? "pm2-profit" : "pm2-loss"}`}>{fmtPL(bookedPL)}</span>
        </div>
      </div>

      {/* Status row */}
      <div className="pm2-status-row">
        <div className="pm2-status-cell">
          <span className="pm2-status-label">Status</span>
          <span className={`pm2-status-value${statusValueMod}`}>{statusText}</span>
        </div>
        <div className="pm2-status-cell">
          <span className="pm2-status-label">Trailing SL</span>
          <span className="pm2-status-value">{fmt(trailingStop)}{tslHit ? " ✓" : ""}</span>
        </div>
      </div>
        </>
      )}
    </div>
    </>
  );
};

export default PrimeMagicOverlay;
